"use client"

import React, { useState } from 'react';
import { cn } from '../../lib/utils';

const DropdownMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Add click handler to close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
      {React.Children.map(children, child => {
        if (child.type.displayName === 'DropdownMenuTrigger') {
          return React.cloneElement(child, {
            onClick: (e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
              child.props.onClick?.(e);
            }
          });
        }
        if (child.type.displayName === 'DropdownMenuContent') {
          return isOpen && React.cloneElement(child, {
            onClick: (e) => e.stopPropagation()
          });
        }
        return child;
      })}
    </div>
  );
};

const DropdownMenuTrigger = React.forwardRef(({ asChild, className, children, ...props }, ref) => {
  const Comp = asChild ? React.cloneElement(children, { ref, ...props }) : (
    <button
      ref={ref}
      className={cn("inline-flex justify-center items-center", className)}
      {...props}
    >
      {children}
    </button>
  );
  return Comp;
});

const DropdownMenuContent = React.forwardRef(({ 
  children, 
  className,
  align = 'center',
  ...props 
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "absolute z-50 mt-2 rounded-md shadow-lg",
        "bg-white border border-gray-200",
        "dark:bg-gray-800 dark:border-gray-700",
        {
          "left-0": align === 'start',
          "right-0": align === 'end',
          "left-1/2 -translate-x-1/2": align === 'center'
        },
        className
      )}
      {...props}
    >
      <div className="py-1">{children}</div>
    </div>
  );
});

const DropdownMenuItem = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "w-full text-left px-4 py-2 text-sm",
        "hover:bg-gray-100 dark:hover:bg-gray-700",
        "text-gray-700 dark:text-gray-200",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});

const DropdownMenuLabel = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "px-4 py-2 text-sm font-medium",
        "text-gray-900 dark:text-gray-100",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("h-px my-1 bg-gray-200 dark:bg-gray-700", className)}
      {...props}
    />
  );
});

DropdownMenu.displayName = "DropdownMenu";
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";
DropdownMenuContent.displayName = "DropdownMenuContent";
DropdownMenuItem.displayName = "DropdownMenuItem";
DropdownMenuLabel.displayName = "DropdownMenuLabel";
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
